import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import VideoCard from "@/components/feature/VideoCard";
import { categoryMeta, getRelatedVideos, getVideoById } from "@/mocks/videos";
import { useFavorites } from "@/hooks/useFavorites";
import { useSEO } from "@/hooks/useSEO";

export default function Detail() {
  const { videoId } = useParams<{ videoId: string }>();
  const video = videoId ? getVideoById(videoId) : undefined;
  const { isFavorite, toggleFavorite } = useFavorites();
  const [toast, setToast] = useState<string | null>(null);
  const [playing, setPlaying] = useState(false);

  useSEO({
    title: video ? `${video.title} - 粉色视频` : "视频详情 - 粉色视频",
    description: video
      ? `${video.title}（${video.year}·${video.region}）：${video.description.slice(0, 60)}……粉色视频带你了解剧情简介与详细信息。`
      : "粉色视频视频详情页",
    keywords: video ? `${video.title},${video.genre.join(",")},粉色视频` : "粉色视频",
  });

  const showToast = (msg: string) => {
    setToast(msg);
    window.setTimeout(() => setToast(null), 2200);
  };

  if (!video) {
    return (
      <div className="mx-auto flex w-full max-w-[1440px] flex-col items-center justify-center px-4 py-32 text-center md:px-8">
        <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-200 text-foreground-500">
          <i className="ri-error-warning-line text-3xl"></i>
        </span>
        <h1 className="mt-5 text-xl font-bold text-foreground-900">未找到该视频</h1>
        <p className="mt-2 text-sm text-foreground-500">视频可能已下架或链接有误</p>
        <Link
          to="/"
          className="mt-6 flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-2.5 text-sm font-semibold text-background-50 hover:bg-primary-600"
        >
          <i className="ri-home-4-line"></i>返回首页
        </Link>
      </div>
    );
  }

  const meta = categoryMeta[video.category];
  const related = getRelatedVideos(video, 5);
  const fav = isFavorite(video.id);

  const handlePlay = () => {
    setPlaying(true);
    showToast("视频播放功能演示中，待接口接入");
  };

  const handleFavorite = () => {
    const added = toggleFavorite(video.id);
    showToast(added ? "已添加到我的收藏" : "已从收藏中移除");
  };

  const handleShare = () => {
    showToast("分享链接已复制（演示）");
  };

  return (
    <div className="w-full">
      <div className="bg-background-100">
        <div className="mx-auto w-full max-w-[1440px] px-4 py-5 md:px-8">
          <nav className="flex flex-wrap items-center gap-1 text-xs text-foreground-500">
            <Link to="/" className="cursor-pointer hover:text-primary-600">首页</Link>
            <i className="ri-arrow-right-s-line"></i>
            <Link to={`/category/${video.category}`} className="cursor-pointer hover:text-primary-600">
              {meta.name}
            </Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-foreground-800">{video.title}</span>
          </nav>
        </div>
      </div>

      <div className="mx-auto w-full max-w-[1440px] px-4 py-8 md:px-8 md:py-10">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <div className="relative aspect-video w-full overflow-hidden rounded-lg bg-foreground-950">
              <img
                src={video.cover}
                alt={`${video.title} 剧照`}
                title={`${video.title} 在线观看`}
                className="h-full w-full object-top opacity-60"
              />
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <button
                  type="button"
                  onClick={handlePlay}
                  className="flex h-16 w-16 cursor-pointer items-center justify-center rounded-full bg-primary-500 text-background-50 transition-transform hover:scale-110 md:h-20 md:w-20"
                  aria-label="播放"
                >
                  <i className="ri-play-fill text-4xl"></i>
                </button>
                <p className="mt-4 rounded-full bg-foreground-950/60 px-4 py-1.5 text-xs text-background-100 backdrop-blur-sm md:text-sm">
                  {playing ? "视频播放功能演示中，待接口接入" : "点击播放（演示区域，暂不支持真实播放）"}
                </p>
              </div>
              <span className="absolute left-3 top-3 rounded-md bg-foreground-950/70 px-2 py-1 text-xs font-medium text-background-50 backdrop-blur-sm">
                {video.duration}
              </span>
            </div>

            <div className="mt-6">
              <div className="flex flex-wrap items-center gap-2">
                <span className="rounded-full bg-primary-100 px-3 py-1 text-xs font-medium text-primary-700">
                  {meta.name}
                </span>
                {video.genre.map((g) => (
                  <span
                    key={g}
                    className="rounded-full bg-secondary-100 px-3 py-1 text-xs font-medium text-secondary-800"
                  >
                    {g}
                  </span>
                ))}
                <span className="rounded-full bg-accent-100 px-3 py-1 text-xs font-medium text-accent-700">
                  {video.status}
                </span>
              </div>

              <h1 className="mt-4 text-2xl font-black text-foreground-950 md:text-3xl">
                {video.title}
              </h1>

              <div className="mt-4 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-foreground-600">
                <span className="flex items-center gap-1">
                  <i className="ri-star-fill text-accent-500"></i>
                  <span className="font-bold text-foreground-900">{video.rating.toFixed(1)}</span> 分
                </span>
                <span className="flex items-center gap-1"><i className="ri-calendar-line"></i>{video.year}</span>
                <span className="flex items-center gap-1"><i className="ri-map-pin-line"></i>{video.region}</span>
                <span className="flex items-center gap-1"><i className="ri-fire-line"></i>{video.popularity} 热度</span>
              </div>

              <div className="mt-6 flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  onClick={handlePlay}
                  className="flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
                >
                  <i className="ri-play-fill text-lg"></i>开始观看
                </button>
                <button
                  type="button"
                  onClick={handleFavorite}
                  className={`flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md px-6 py-2.5 text-sm font-semibold transition-colors ${
                    fav
                      ? "bg-primary-100 text-primary-700"
                      : "border border-background-300 text-foreground-800 hover:border-primary-300 hover:text-primary-600"
                  }`}
                >
                  <i className={`${fav ? "ri-heart-3-fill" : "ri-heart-3-line"} text-lg`}></i>
                  {fav ? "已收藏" : "收藏"}
                </button>
                <button
                  type="button"
                  onClick={handleShare}
                  className="flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-6 py-2.5 text-sm font-semibold text-foreground-800 transition-colors hover:border-primary-300 hover:text-primary-600"
                >
                  <i className="ri-share-forward-line text-lg"></i>分享
                </button>
                <Link
                  to={`/category/${video.category}`}
                  className="flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-6 py-2.5 text-sm font-semibold text-foreground-800 transition-colors hover:border-primary-300 hover:text-primary-600"
                >
                  <i className="ri-arrow-left-line text-lg"></i>返回{meta.name}
                </Link>
              </div>
            </div>

            <div className="mt-8 rounded-lg bg-background-100 p-5 md:p-6">
              <h2 className="text-base font-bold text-foreground-950">剧情简介</h2>
              <p className="mt-3 text-sm leading-relaxed text-foreground-700">{video.description}</p>
            </div>
          </div>

          <aside className="lg:col-span-1">
            <div className="rounded-lg border border-background-200/80 p-5 md:p-6">
              <h2 className="text-base font-bold text-foreground-950">关键信息</h2>
              <dl className="mt-4 space-y-4">
                <div>
                  <dt className="text-xs text-foreground-500">类型</dt>
                  <dd className="mt-1 text-sm font-medium text-foreground-900">{video.genre.join(" / ")}</dd>
                </div>
                <div>
                  <dt className="text-xs text-foreground-500">主演</dt>
                  <dd className="mt-1 text-sm font-medium text-foreground-900">{video.cast.join("、")}</dd>
                </div>
                <div>
                  <dt className="text-xs text-foreground-500">导演</dt>
                  <dd className="mt-1 text-sm font-medium text-foreground-900">{video.director}</dd>
                </div>
                <div>
                  <dt className="text-xs text-foreground-500">地区</dt>
                  <dd className="mt-1 text-sm font-medium text-foreground-900">{video.region}</dd>
                </div>
                <div>
                  <dt className="text-xs text-foreground-500">年份 / 更新</dt>
                  <dd className="mt-1 text-sm font-medium text-foreground-900">
                    {video.year} · {video.status} · {video.updateDate}
                  </dd>
                </div>
              </dl>
            </div>
          </aside>
        </div>

        <section className="mt-12">
          <div className="mb-6 flex items-center gap-2">
            <span className="flex h-6 w-1.5 rounded-full bg-accent-500"></span>
            <h2 className="text-xl font-black text-foreground-950 md:text-2xl">相关推荐</h2>
          </div>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 md:gap-5 lg:grid-cols-5">
            {related.map((rv) => (
              <VideoCard key={rv.id} video={rv} showFavorite={false} />
            ))}
          </div>
        </section>
      </div>

      {toast && (
        <div className="animate-fade-in fixed bottom-8 left-1/2 z-[60] -translate-x-1/2 rounded-full bg-foreground-950 px-5 py-3 text-sm font-medium text-background-50 shadow-lg">
          <span className="flex items-center gap-2">
            <i className="ri-information-line"></i>
            {toast}
          </span>
        </div>
      )}
    </div>
  );
}